import React, { useEffect, useMemo, useRef, useState } from "react";
import { sentenceCaseLabel, useTranslation } from "../i18n";

import DataTableLoadingRow from "../Components/DataTableLoadingRow";
import { generatePath, useNavigate } from "react-router-dom";
import { RoutesPaths, backendUrl } from "../Router/constant";
import { useDispatch, useSelector } from "react-redux";
import { getOrderListAction } from "../actions/order";

const getOrderText = (value) => {
  if (typeof value === "string" || typeof value === "number") return String(value);
  return value?.name || value?.title || value?.username || value?.companyName || value?.email || "";
};

const getOrderImageUrl = (image) => {
  const toPublicUrl = (value) => {
    if (typeof value !== "string") return "";
    const normalized = value.trim();
    if (!normalized || /^[a-z]:[\\/]/i.test(normalized) || /^\\\\/.test(normalized)) return "";
    if (/^(https?:|data:|blob:)/i.test(normalized)) return normalized;
    if (normalized.startsWith("/uploads/")) return `${backendUrl}${normalized}`;
    if (normalized.startsWith("/")) return normalized;
    return `${backendUrl}/uploads/${encodeURIComponent(normalized)}`;
  };

  if (typeof image === "string") {
    return toPublicUrl(image);
  }
  if (!image || typeof image !== "object") return "";

  const directUrl = image.url || image.secure_url || image.src;
  if (typeof directUrl === "string") {
    const url = toPublicUrl(directUrl);
    if (url) return url;
  }

  const imagePath = toPublicUrl(image.path);
  if (imagePath) {
    if (!/^[a-z]:[\\/]/i.test(String(image.path)) && !/^\\\\/.test(String(image.path))) {
      return imagePath;
    }
  }

  const imageId = image.uploadId || image.fileId || image.imageId || image._id || image.id;
  return imageId ? `${backendUrl}/uploads/${encodeURIComponent(String(imageId))}` : "";
};

const formatOrderQuantity = (quantity, unit) => {
  const value = quantity && typeof quantity === "object"
    ? quantity.amount ?? quantity.value ?? quantity.quantity
    : quantity;
  const quantityUnit =
    (quantity && typeof quantity === "object" ? quantity.unit : "") || unit;
  if (value === undefined || value === null || value === "") return "—";
  return `${value}${quantityUnit ? ` ${getOrderText(quantityUnit) || quantityUnit}` : ""}`;
};

const formatOrderDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
};

const readCount = (counts, keys) => {
  const key = keys.find((candidate) =>
    counts?.[candidate] !== undefined && counts?.[candidate] !== null && counts?.[candidate] !== "",
  );
  if (!key) return undefined;
  const count = Number(counts[key]);
  return Number.isFinite(count) ? count : undefined;
};

const getOrderStatus = (order) =>
  String(order?.currentStatus || order?.status || order?.orderStatus || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const getOrderAlertStatus = (order) =>
  String(order?.vendorResponse?.status || order?.approvalStatus || order?.status || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const mapVendorOrder = (order) => {
  const requirements = Array.isArray(order?.requirements) ? order.requirements : [];
  const firstRequirement = requirements[0] || {};
  const requirementProducts = Array.isArray(firstRequirement.products)
    ? firstRequirement.products
    : [];
  const lines = order?.items || order?.products || order?.orderItems || order?.lines || [];
  const firstLine = requirementProducts[0] || (Array.isArray(lines) ? lines[0] : null) || {};
  const product =
    firstLine?.product || firstLine?.productId || firstLine?.item ||
    order?.product || order?.productId || firstLine || {};
  const productName =
    product?.name || product?.productName || product?.title ||
    getOrderText(product) || firstLine?.productName || firstLine?.name ||
    order?.productName || order?.itemName || order?.commodity || order?.title || "—";
  const sector = firstRequirement?.sector || firstRequirement?.sectorId ||
    product?.sector || order?.sector || order?.sectorName;
  const sectorName = getOrderText(sector) || firstRequirement?.sectorName ||
    product?.sectorName || order?.sectorName || "—";
  const image = firstLine?.mainImage || firstLine?.image || firstLine?.thumbnail ||
    product?.mainImage || product?.image || product?.thumbnail ||
    firstRequirement?.mainImage || order?.productImage || order?.mainImage || order?.image || "";
  const quantity = formatOrderQuantity(
    firstRequirement?.quantity ?? firstLine?.quantity ?? product?.quantity ?? order?.quantity,
    firstRequirement?.unit || firstRequirement?.quantityUnit || firstLine?.unit ||
      firstLine?.quantityUnit || product?.unit || order?.unit || order?.quantityUnit,
  );
  const rawStatus = getOrderText(order?.currentStatus || order?.status || order?.orderStatus || order?.currentStage || order?.stage) || "Pending";
  const currentStatusLabel =
    getOrderText(order?.currentStatusLabel) ||
    (typeof order?.currentStatusLabel?.label === "string"
      ? order.currentStatusLabel.label
      : "");
  const statusValue = rawStatus.trim().toLowerCase().replace(/[\s-]+/g, "_");
  const displayStatusValue = statusValue === "in_transit" ? "pending" : statusValue;
  const statusClasses = {
    pending: "pending",
    accepted: "accepted",
    approved: "accepted",
    completed: "completed",
    complete: "completed",
    delivered: "completed",
    order_delivered: "completed",
    initiated: "pending",
    order_created: "pending",
    order_placed: "pending",
    cancelled: "cancelled",
    canceled: "cancelled",
    rejected: "cancelled",
  };
  const orderId = order?.trackingId || order?.orderNumber || order?.orderNo || order?.referenceNumber || order?.code || order?.id || order?._id || "Order";
  const routeId = order?._id || order?.id || order?.orderId || orderId;
  const buyer = order?.buyer || order?.client || order?.customer || order?.user || order?.createdBy;
  const imageUrl = getOrderImageUrl(image);

  return {
    id: String(orderId),
    routeId: String(routeId),
    createdDate: formatOrderDate(order?.createdAt || order?.createdOn || order?.orderDate || order?.date),
    commodity: productName,
    sectorName,
    quantity,
    seller: getOrderText(buyer) || order?.buyerName || order?.clientName || order?.customerName || "—",
    currentStage: sentenceCaseLabel(currentStatusLabel || displayStatusValue),
    statusValue: displayStatusValue,
    stageClass: statusClasses[displayStatusValue] || displayStatusValue.replace(/_/g, "-"),
    image: imageUrl,
  };
};
const EMPTY_ORDERS = [];
const EMPTY_COUNTS = {};

export default function Operations() {
  const { t, text, status: translateStatus } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const orderState = useSelector((state) => state.order || {});
  const orders = Array.isArray(orderState.items) ? orderState.items : EMPTY_ORDERS;
  const counts = orderState.counts || EMPTY_COUNTS;
  const pagination = orderState.pagination || {};
  const loading = Boolean(orderState.loading);
  const error = orderState.error || "";
  const [page, setPage] = useState(1);
  const limit = 10;
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);

  const stats = useMemo(() => {
    const hasStatus = (order, statuses) =>
      statuses.includes(getOrderStatus(order)) || statuses.includes(getOrderAlertStatus(order));
    const pendingFromOrders = orders.filter((order) =>
      hasStatus(order, ["pending", "in_transit"]),
    ).length;
    const acceptedFromOrders = orders.filter((order) =>
      hasStatus(order, ["accepted", "approved"]),
    ).length;
    const cancelledFromOrders = orders.filter((order) =>
      hasStatus(order, ["cancelled", "canceled"]),
    ).length;
    const completedFromOrders = orders.filter((order) =>
      hasStatus(order, ["completed", "complete", "delivered"]),
    ).length;

    const totalCount =
      readCount(counts, ["total", "totalOrders"]) ??
      (Number(pagination.total) || orders.length);
    const pendingCount =
      readCount(counts, ["pending", "pendingOrders", "pendingCount"]) ??
      pendingFromOrders;
    const acceptedCount =
      readCount(counts, ["accepted", "acceptedOrders", "acceptedCount"]) ??
      acceptedFromOrders;
    const completedCount =
      readCount(counts, ["completed", "completedOrders", "delivered"]) ??
      completedFromOrders;
    const cancelledCount =
      readCount(counts, ["cancelled", "canceled", "cancelledOrders"]) ??
      cancelledFromOrders;

    return [
      { title: "Total", value: totalCount, change: "All orders", icon: "/assets/operations-icon.svg", color: "gold" },
      { title: "Pending", value: pendingCount, change: "Pending orders", icon: "/assets/shipment-icon.svg", color: "pending" },
      { title: "Accepted", value: acceptedCount, change: "Accepted orders", icon: "/assets/check-circle.svg", color: "blue" },
      { title: "Completed", value: completedCount, change: "Completed orders", icon: "/assets/check-circle.svg", color: "completed" },
      { title: "Cancelled", value: cancelledCount, change: "Cancelled orders", icon: "/assets/alert-icon.svg", color: "cancelled" },
    ];
  }, [counts, orders, pagination.total]); 

  useEffect(() => {
    dispatch(getOrderListAction({ page, limit, status: statusFilter || "all" }));
  }, [dispatch, page, statusFilter]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!statusDropdownRef.current?.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const orderRows = useMemo(() => orders.map(mapVendorOrder), [orders]);
  const filteredOperations = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (!value) return orderRows;
    return orderRows.filter((item) =>
      [item.id, item.commodity, item.sectorName, item.quantity, item.seller, item.currentStage]
        .some((field) => field.toLowerCase().includes(value)),
    );
  }, [orderRows, search]);

  const statusOptions = useMemo(() => {
    const knownStatuses = ["pending","completed", "cancelled"];
    return Array.from(new Set([...knownStatuses, ...orderRows.map((item) => item.statusValue).filter(Boolean)]));
  }, [orderRows]);
  const total = Number(pagination.total) || 0;
  const currentPage = Number(pagination.page) || page;
  const totalPages = Math.max(Number(pagination.totalPages) || 1, 1);
  const pageWindowStart = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
  const pageNumbers = Array.from(
    { length: Math.min(totalPages, 5) },
    (_, index) => pageWindowStart + index,
  );
  const firstItem = total ? (currentPage - 1) * limit + 1 : 0;
  const lastItem = total ? Math.min((currentPage - 1) * limit + filteredOperations.length, total) : 0;
  return (
    <div className="dashboard-page inquiries-page">
      <div className="inquiries-page-header">
        <div>
          <h1 className="f-s-28 l-h-32 fw-semibold">{t("ui.active.order")}</h1>
          <p className="f-s-14 l-h-20">{t("ui.track.and.manage.all.your.ongoing.trade.operations.in.one.place")}</p>
        </div>
      </div>
      <div className="container-fluid px-0 mb-4">
        <div className="row row-cols-2 row-cols-lg-5 g-3">
          {stats.map((stat, index) => (
            <div
              className="col"
              key={index}
            >
              {/* <div className="operation-stat-card h-100">
                <div className={`operation-stat-icon ${stat.colorClass}`}>
                  <img src={stat.icon} alt="" />
                </div>

                <div className="operation-stat-content">
                  <span className="operation-stat-title">{stat.title}</span>

                  <strong className="operation-stat-value">{stat.value}</strong>

                  <span
                    className={`operation-stat-growth ${
                      stat.growth === "up" ? "positive" : "negative"
                    }`}
                  >
                    <span className="growth-arrow">
                      {stat.growth === "up" ? "↗" : "↘"}
                    </span>

                    {stat.description}
                  </span>
                </div>
              </div> */}
               <div className="overview-stat-card">
              <div className={`overview-stat-icon ${stat.color}`}>
                <span
                  className={`overview-stat-icon-img ${stat.color}`}
                  style={{
                    "--icon-url": `url(${stat.icon})`,
                  }}
                />
              </div>

              <div className="overview-stat-content">
                <span className="overview-stat-title">{text(stat.title)}</span>

                <div className="overview-stat-bottom">
                  <strong>{stat.value}</strong>
                  <small className={stat.color}>{text(stat.change)}</small>
                </div>
              </div>
            </div>
            </div>
          ))}
        </div>
      </div>
      <div className={"inquiries-table-card" + (statusDropdownOpen ? " has-open-status-dropdown" : "")}>
        <div className="inquiries-table-header">
          <div>
            <h2>{t("ui.order.details")}</h2>
            <p>{" "}{t("ui.view.and.manage.your.recent.orders")}{" "}</p>
          </div>
          <div className="inquiries-table-actions">
            <div className="inquiries-search">
              <img src="/assets/search-icon.svg" alt="" />
              <input
                type="text"
                placeholder={t("ui.search.orders")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button type="button" onClick={() => setSearch("")}>
                  ×
                </button>
              )}
            </div>
            <div
              className={"inquiries-status-filter" + (statusDropdownOpen ? " is-open" : "")}
              ref={statusDropdownRef}
            >
              <button
                type="button"
                className="inquiries-status-trigger"
                aria-label={t("ui.filter.orders.by.status")}
                aria-haspopup="listbox"
                aria-expanded={statusDropdownOpen}
                onClick={() => setStatusDropdownOpen((open) => !open)}
              >
                <span>
                  {statusFilter
                    ? translateStatus(sentenceCaseLabel(statusFilter))
                    : t("ui.allStatus")}
                </span>
                <svg viewBox="0 0 14 14" aria-hidden="true">
                  <path d="m3.5 5.25 3.5 3.5 3.5-3.5" />
                </svg>
              </button>
              {statusDropdownOpen && (
                <div className="inquiries-status-menu" role="listbox" aria-label={t("ui.filter.orders.by.status")}>
                  {[{ value: "", label: t("ui.allStatus") }, ...statusOptions.map((status) => ({
                    value: status,
                    label: sentenceCaseLabel(status),
                  }))].map((option) => (
                    <button
                      type="button"
                      key={option.value || "all"}
                      className={"inquiries-status-option" + (statusFilter === option.value ? " is-selected" : "")}
                      role="option"
                      aria-selected={statusFilter === option.value}
                      onClick={() => {
                        setStatusFilter(option.value);
                        setPage(1);
                        setStatusDropdownOpen(false);
                      }}
                    >
                      {translateStatus(option.label)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="inquiries-table-wrapper">
          <table className="inquiries-modern-table">
            <thead>
              <tr>
                <th>{t("ui.product.details")}</th>
                <th>{t("ui.quantity")}</th>
                <th>{t("ui.client")}</th>
                <th>{t("ui.current.stage")}</th>
                {/* <th>Next Step</th> */}
                {/* <th>Est. Completion</th> */}
                <th className="inquiry-actions-heading">{t("ui.actions.8bdtct")}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <DataTableLoadingRow colSpan={5} label={t("ui.loadingOrders")} />
              ) : error ? (
                <tr><td colSpan="5"><div className="inquiries-empty-state" role="alert"><strong>{t("ui.unable.to.load.orders")}</strong><span>{text(error)}</span></div></td></tr>
              ) : filteredOperations.length > 0 ? (
                filteredOperations.map((item) => (
                  <tr key={item.routeId}>
                    <td>
                      <div className="operation-table-cell">
                        <div className="operation-table-image">
                          {item.image && (
                            <img src={item.image} alt={item.commodity} onError={(event) => { event.currentTarget.style.display = "none"; }} />
                          )}
                        </div>
                        <div className="operation-table-info">
                          <strong>{item.commodity}</strong>
                          <span>{item.sectorName}</span>

                        </div>
                      </div>
                    </td>
                    <td><div className="operation-quantity">{item.quantity}</div></td>
                    <td><div className="operation-seller-cell"><strong>{item.seller}</strong></div></td>
                    <td><div className="operation-stage-cell"><span className={`operation-stage-status ${item.stageClass}`}>{translateStatus(item.currentStage)}</span></div></td>
                    <td>
                      <div className="operation-table-actions">
                        <button type="button" className="operation-view-btn" aria-label={`View order ${item.id}`} onClick={() => navigate(generatePath(RoutesPaths.ORDERDETAILS, { id: item.routeId }))}>
                          <img src="/assets/eye.svg" alt="" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="5"><div className="inquiries-empty-state"><img src="/assets/search-icon.svg" alt="" /><strong>{t("ui.no.orders.found")}</strong><span>{t("ui.try.changing.your.search.or.filters")}</span></div></td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="inquiries-table-footer">
          <span>{t("ui.showing.1ui8snv")}{" "}{firstItem}{" "}{t("ui.to")}{" "}{lastItem}{" "}{t("ui.of")}{" "}<strong>{total}</strong>{" "}{t("ui.orders")}</span>
          <div className="inquiries-pagination">
            <button type="button" disabled={currentPage <= 1 || loading} onClick={() => setPage(currentPage - 1)} aria-label={t("ui.previous.page")}>‹</button>
            {pageNumbers.map((pageNumber) => (
              <button type="button" key={pageNumber} className={pageNumber === currentPage ? "active" : ""} aria-current={pageNumber === currentPage ? "page" : undefined} disabled={loading} onClick={() => setPage(pageNumber)}>{pageNumber}</button>
            ))}
            <button type="button" disabled={currentPage >= totalPages || loading} onClick={() => setPage(currentPage + 1)} aria-label={t("ui.next.page")}>›</button>
          </div>
        </div>
      </div>
    </div>
  );
}
